import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { sendGAEvent } from '@common/utlis';
import { defaultFetchedState, isSortingOrder } from '@utils/productList';

import type {
  activeFilters,
  productsAndAvailableFilters,
  reducerFunctions,
  sortingOrder
} from '@utils/productList';
import type { NextRouter } from 'next/router';

const getFiltersFromUrl = (router: NextRouter): activeFilters => {
  try {
    const filters = (router.query.filters as string | undefined) || '{}';
    const parsedFiltersObject = JSON.parse(filters);
    return cleanActiveFiltersObject(parsedFiltersObject);
  } catch (e) {
    console.error(e);
    return {};
  }
};

// TODO account for the sorting order
export const getNumberOfActiveFilters = (activeFilters: activeFilters): number =>
  Object.values(activeFilters).reduce((acc, curr) => acc + curr.length, 0);

const cleanActiveFiltersObject = (activeFilters: activeFilters): activeFilters => {
  Object.keys(activeFilters)
    .filter((key) => activeFilters[key].length === 0)
    .forEach((key) => delete activeFilters[key]);

  return activeFilters;
};

const getCurrentPageFromUrl = (router: NextRouter) => {
  const currentPage = Number(router.query.page);
  return Number.isNaN(currentPage) ? 1 : currentPage;
};

const getSortingOrderFromUrl = (router: NextRouter) => {
  const currentSortingMethod = router.query.sort;
  return isSortingOrder(currentSortingMethod) ? currentSortingMethod : undefined;
};

export type action =
  | { type: 'clearFilters' }
  | { type: 'checkFilter' | 'uncheckFilter' | 'selectRadio'; section: string; option: string }
  | { type: 'setCurrentPage'; page: number }
  | { type: 'setCurrentSortingOrder'; sortingOrder: sortingOrder }
  | { type: 'update'; updatedState: ActiveFiltersAndSorting };

const activeFiltersAndSortingReducer = (
  activeFiltersAndSorting: ActiveFiltersAndSorting,
  action: action
): ActiveFiltersAndSorting => {
  switch (action.type) {
    case 'checkFilter': {
      const { section, option: optionToAdd } = action;
      const { activeFilters } = activeFiltersAndSorting;
      if (!section || !optionToAdd) {
        throw new Error('checkFilter - mandatory parameter `section` or `optionToAdd` missing');
      }
      const checkedOptions = [...(activeFilters[section] || []), optionToAdd];
      const parsed = { ...activeFilters, [section]: checkedOptions };
      return {
        ...activeFiltersAndSorting,
        activeFilters: cleanActiveFiltersObject(parsed),
        currentPage: 1
      };
    }
    case 'clearFilters': {
      return { ...activeFiltersAndSorting, activeFilters: {}, currentPage: 1 };
    }
    case 'selectRadio': {
      const { section, option: optionToAdd } = action;
      const { activeFilters } = activeFiltersAndSorting;
      if (!section || !optionToAdd) {
        throw new Error('selectRadio: mandatory parameter `section` or `optionToAdd` missing');
      }
      const parsed = { ...activeFilters, [section]: optionToAdd === 'all' ? [] : [optionToAdd] };
      return {
        ...activeFiltersAndSorting,
        activeFilters: cleanActiveFiltersObject(parsed),
        currentPage: 1
      };
    }
    case 'setCurrentPage': {
      const { page } = action;
      if (!page || typeof page !== 'number') {
        throw new Error('setCurrentPage: mandatory parameter `page` missing or not a number');
      }
      return { ...activeFiltersAndSorting, currentPage: page };
    }

    case 'setCurrentSortingOrder': {
      const { sortingOrder } = action;
      if (!sortingOrder || !isSortingOrder(sortingOrder)) {
        throw new Error(
          'setCurrentSortingOrder: mandatory parameter `sortingOrder` missing or not of type sortingOrder'
        );
      }
      return { ...activeFiltersAndSorting, currentSortingOrder: sortingOrder };
    }
    case 'uncheckFilter': {
      const { section, option: optionToRemove } = action;
      const { activeFilters } = activeFiltersAndSorting;
      if (!section || !optionToRemove) {
        throw new Error('uncheckFilter - mandatory parameter `section` or `optionToRemove` missing');
      }
      const checkedOptions = (activeFilters[section] || []).filter((option) => option !== optionToRemove);
      const parsed = { ...activeFilters, [section]: checkedOptions };
      return {
        ...activeFiltersAndSorting,
        activeFilters: cleanActiveFiltersObject(parsed),
        currentPage: 1
      };
    }
    case 'update': {
      return { ...activeFiltersAndSorting, ...action.updatedState };
    }
  }
};

const getReducerFunctions = (dispatchActiveFiltersAndSorting): reducerFunctions => ({
  clearFilters: () => {
    dispatchActiveFiltersAndSorting({ type: 'clearFilters' });
    // dispatchActiveFiltersAndSorting({ type: 'setCurrentSortingOrder', sortingOrder: 'relevance_desc' }); //TODO
    sendGAEvent({
      category: 'Search Filter',
      action: 'filter clear all'
    });
  },
  checkFilter: ({ section, option }) => {
    dispatchActiveFiltersAndSorting({ type: 'checkFilter', section, option });
    sendGAEvent({
      category: 'Search Filter',
      action: 'filter switched',
      label: section,
      additionalTrackingParameters: {
        additionalTrackingParameter1: option,
        additionalTrackingParameter2: 'check'
      }
    });
  },
  uncheckFilter: ({ section, option }) => {
    dispatchActiveFiltersAndSorting({ type: 'uncheckFilter', section, option });
    sendGAEvent({
      category: 'Search Filter',
      action: 'filter switched',
      label: section,
      additionalTrackingParameters: {
        additionalTrackingParameter1: option,
        additionalTrackingParameter2: 'uncheck'
      }
    });
  },
  selectRadio: ({ section, option }) => {
    dispatchActiveFiltersAndSorting({ type: 'selectRadio', section, option });
    sendGAEvent({
      category: 'Search Filter',
      action: 'filter switched',
      label: section,
      additionalTrackingParameters: {
        additionalTrackingParameter1: option,
        additionalTrackingParameter2: 'check'
      }
    });
  },
  setCurrentPage: (page: number) => {
    dispatchActiveFiltersAndSorting({ type: 'setCurrentPage', page });
  },
  setCurrentSortingOrder: (sortingOrder: sortingOrder) => {
    dispatchActiveFiltersAndSorting({ type: 'setCurrentSortingOrder', sortingOrder });
    sendGAEvent({ category: 'Search', action: 'Sorting changed', label: sortingOrder });
  }
});

export type UseProductListParams = {
  initialState?: productsAndAvailableFilters;
  fetchNextState: (currentState: ActiveFiltersAndSorting) => Promise<productsAndAvailableFilters>;
  defaultSortingOrder?: sortingOrder;
};

export type ActiveFiltersAndSorting = {
  activeFilters: activeFilters;
  currentPage: number;
  currentSortingOrder: sortingOrder;
};

export type ProductListState = {
  productsAndAvailableFilters: productsAndAvailableFilters;
  activeFiltersAndSorting: ActiveFiltersAndSorting;
  reducerFunctions: reducerFunctions;
};

export const useProductList = ({
  initialState,
  fetchNextState,
  defaultSortingOrder
}: UseProductListParams): ProductListState => {
  const router = useRouter();

  // TODO: Use react query with ssr for that https://tanstack.com/query/v4/docs/react/guides/ssr

  const [productsAndAvailableFilters, setProductsAndAvailableFilters] = useState(
    initialState || defaultFetchedState
  );

  const [activeFiltersAndSorting, dispatchActiveFiltersAndSorting] = useReducer(
    activeFiltersAndSortingReducer,
    {
      activeFilters: getFiltersFromUrl(router),
      currentPage: getCurrentPageFromUrl(router),
      currentSortingOrder: getSortingOrderFromUrl(router) ?? defaultSortingOrder ?? 'relevance_desc'
    }
  );

  const reducerFunctions = useMemo(() => getReducerFunctions(dispatchActiveFiltersAndSorting), []);
  const urlAndStateDiverge = useMemo(() => {
    const filtersDiverge = !isEqual(activeFiltersAndSorting.activeFilters, getFiltersFromUrl(router));
    const pageDiverges = activeFiltersAndSorting.currentPage !== getCurrentPageFromUrl(router);
    const sortingOrderDiverges =
      activeFiltersAndSorting.currentSortingOrder !==
      (getSortingOrderFromUrl(router) ?? defaultSortingOrder ?? 'relevance_desc');
    return filtersDiverge || pageDiverges || sortingOrderDiverges;
  }, [
    activeFiltersAndSorting.activeFilters,
    activeFiltersAndSorting.currentPage,
    activeFiltersAndSorting.currentSortingOrder,
    defaultSortingOrder,
    router
  ]);

  const pushURLQuery = useCallback(
    async (router: NextRouter, browserSearchParams, activeFiltersAndSorting: ActiveFiltersAndSorting) => {
      const { activeFilters, currentPage, currentSortingOrder } = activeFiltersAndSorting;
      const query = {
        ...omit(router.query, ['store', 'categoryId', 'sort', 'filters', 'page']),
        ...omit(browserSearchParams, ['store', 'categoryId', 'sort', 'filters', 'page']),
        ...(typeof activeFilters !== 'undefined' &&
          getNumberOfActiveFilters(activeFilters) > 0 && { filters: JSON.stringify(activeFilters) }),
        ...(typeof currentPage === 'number' && currentPage !== 1 && { page: currentPage.toString() }),
        ...(isSortingOrder(currentSortingOrder) &&
          currentSortingOrder !== (defaultSortingOrder ?? 'relevance_desc') && { sort: currentSortingOrder })
      };
      const queryString = new URLSearchParams(query).toString();
      const pathname = window.location.pathname;
      await router.push({ query: router.query }, `${pathname}${queryString && `?${queryString}`}`, {
        shallow: true
      });
    },
    [defaultSortingOrder]
  );

  useEffect(() => {
    setProductsAndAvailableFilters(initialState || defaultFetchedState);
    if (urlAndStateDiverge) {
      dispatchActiveFiltersAndSorting({
        type: 'update',
        updatedState: {
          activeFilters: getFiltersFromUrl(router),
          currentPage: getCurrentPageFromUrl(router),
          currentSortingOrder: getSortingOrderFromUrl(router) ?? defaultSortingOrder ?? 'relevance_desc'
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState]);

  useEffect(() => {
    fetchNextState(activeFiltersAndSorting)
      .then((fetchedState) => setProductsAndAvailableFilters(fetchedState))
      .catch((error) => {
        console.error(error);
      });

    // some GET parameters (e.g. wkz, utm, etc) are striped away from varnish and
    // we only have them in the browser location, not in router.query
    const params = new URL(document.location.toString()).searchParams;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const queryParams = [...params.keys()].reduce((acc, key) => {
      acc[key] = params.get(key);
      return acc;
    }, {});

    pushURLQuery(router, queryParams, activeFiltersAndSorting).catch((error) => console.error(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchNextState is a dependency, but we don't want to refetch on every render
  }, [activeFiltersAndSorting]);

  return {
    productsAndAvailableFilters,
    activeFiltersAndSorting,
    reducerFunctions
  };
};
